/**
 * StructuredContentRenderer - Intelligent content renderer for chat messages
 * 
 * Detects structured JSON responses from agents/tools and renders them
 * using appropriate UI components instead of raw JSON text.
 * 
 * Supported content types:
 * - run_triggered: RunSummaryCard
 * - plan_created: PlanCreatedCard
 * - correction_plan_created: CorrectionPlanCreatedCard
 * - review_complete: ReviewCompleteCard
 * - tests_analyzed: TestsAnalyzedCard
 * - clarification_needed: ClarificationNeededCard
 * - task_created (success + sessionId): TaskCreatedCard  
 * - work_item (id + title): WorkItemCard
 * - code_search (results array): CodeSearchResult
 * - sessions_list: SessionListResult
 * - runs_list: RunListResult
 * - error (success: false): ErrorCard
 * - unknown JSON: RawJsonCard
 * - plain text: Markdown rendering
 */
import { memo, useMemo } from 'react'
import { MarkdownContent } from '@/components/common/MarkdownContent'
import { cn } from '@/lib/utils'

// Import chatbot components for structured rendering
import { RunSummaryCard, type RunSummary } from './RunSummaryCard'
import { TaskCreatedCard, type CreatedTask } from './TaskCreatedCard'
import { WorkItemCard, type WorkItem } from './WorkItemCard'
import { CodeSearchResult, type CodeSearchResultItem } from './CodeSearchResult'
import { SessionListResult, type SessionListItem } from './SessionListResult'
import { RunListResult, type RunListItem } from './RunListResult'

// Import action card components
import { 
  ReviewCompleteCard, 
  ReviewBlockedCard,
  ClarificationNeededCard, 
  PlanCreatedCard,
  type PlanCreatedData,
  CorrectionPlanCreatedCard,
  type CorrectionPlanCreatedData,
  TestsAnalyzedCard,
  ErrorCard, 
  RawJsonCard 
} from './actions'

export interface StructuredContentRendererProps {
  /** The content to render (may be plain text, markdown, or JSON) */
  content: string
  /** Additional CSS classes */
  className?: string
}

// =============================================================================
// JSON Parsing
// =============================================================================

/**
 * Try to parse content as JSON
 */
function tryParseJson(content: string): unknown | null {
  const trimmed = content.trim()
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null
  }
  
  try {
    return JSON.parse(trimmed)
  } catch {
    return null
  }
}

// =============================================================================
// Type Guards - Detection functions for known JSON structures
// =============================================================================

function isRunTriggered(obj: unknown): obj is { 
  action: 'run_triggered'
  data: { run_id: string; repository: string; goal: string }
  status: string 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.action === 'run_triggered' && 
    typeof o.data === 'object' && o.data !== null &&
    typeof (o.data as Record<string, unknown>).run_id === 'string'
}

function isPlanCreated(obj: unknown): obj is { 
  action: 'plan_created'
  data: { 
    goal?: string
    summary?: string
    sub_tasks?: Array<{ order: number; description: string; repository?: string }>
    plan?: {
      title?: string
      objectives?: string[]
      steps?: Array<{ id: string; action: string; file?: string; description: string; details?: string }>
    }
    file_changes?: Array<{ path: string; type: string; description: string }>
    implementation_steps?: Array<string>
    repositories?: Array<{ url: string; relevance: string }>
  }
  reasoning?: string
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  if (o.action !== 'plan_created' || typeof o.data !== 'object' || o.data === null) {
    return false
  }
  const data = o.data as Record<string, unknown>
  // Support multiple formats:
  // 1. Old format: sub_tasks array
  // 2. New format: plan.steps array
  // 3. File changes format: file_changes + implementation_steps
  return Array.isArray(data.sub_tasks) || 
    (typeof data.plan === 'object' && data.plan !== null && Array.isArray((data.plan as Record<string, unknown>).steps)) ||
    Array.isArray(data.file_changes) ||
    Array.isArray(data.implementation_steps)
}

function isCorrectionPlanCreated(obj: unknown): obj is { 
  action: 'correction_plan_created'
  data: { 
    correction_context: string
    original_plan_id?: string
    fixes: Array<{
      fix_id: string
      original_plan_item: string
      issue_type: string
      goal: string
      files_to_modify: string[]
      specific_instructions: string
      complexity?: string
    }>
    constraints?: string[]
  }
  reasoning?: string
  status?: string
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  if (o.action !== 'correction_plan_created' || typeof o.data !== 'object' || o.data === null) {
    return false
  }
  const data = o.data as Record<string, unknown>
  return typeof data.correction_context === 'string' && Array.isArray(data.fixes)
}

function isReviewBlocked(obj: unknown): obj is { 
  action: 'review_blocked'
  approved: false
  reason?: string
  details?: string
  recommendation?: string
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.action === 'review_blocked'
}

function isReviewComplete(obj: unknown): obj is { 
  action: 'review_complete'
  data: { 
    approved: boolean
    verdict: string
    summary: string
    high_severity_issues?: Array<string>
    medium_low_issues?: Array<string>
    comments_posted?: number
    files_reviewed?: number
    constraints_checked?: Record<string, { satisfied: boolean; details: string }>
  }
  reasoning?: string
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.action === 'review_complete' && 
    typeof o.data === 'object' && o.data !== null &&
    typeof (o.data as Record<string, unknown>).verdict === 'string'
}

function isTestsAnalyzed(obj: unknown): obj is { 
  action: 'tests_analyzed'
  data: { 
    total_tests?: number
    passed?: number
    failed?: number
    skipped?: number
    coverage_percent?: number | null
    failures?: Array<string>
    coverage_gaps?: Array<string>
    constraints_satisfied?: Record<string, boolean>
    test_infrastructure?: {
      build_successful?: boolean
      test_command?: string
      exit_code?: number
    }
  }
  reasoning?: string
  warnings?: Array<string>
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.action === 'tests_analyzed' && 
    typeof o.data === 'object' && o.data !== null
}

function isClarificationNeeded(obj: unknown): obj is { 
  action: 'clarification_needed'
  data: { 
    question: string
    intent_type?: string
    suggested_responses?: Array<string>
    missing_info?: Array<string>
  }
  reasoning?: string
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.action === 'clarification_needed' && 
    typeof o.data === 'object' && o.data !== null &&
    typeof (o.data as Record<string, unknown>).question === 'string'
}

function isTaskCreated(obj: unknown): obj is { 
  success: true
  sessionId: string
  status: string
  message: string
  url: string
  linkedWorkItem?: number 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.success === true && typeof o.sessionId === 'string' && typeof o.url === 'string'
}

function isWorkItem(obj: unknown): obj is { 
  id: number
  title: string
  state: string
  type: string 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return typeof o.id === 'number' && typeof o.title === 'string' && typeof o.state === 'string'
}

function isCodeSearchResults(obj: unknown): obj is { 
  results: Array<{ path: string; repository: string; matches: Array<{ line: number; content: string }> }>
  totalCount: number 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return Array.isArray(o.results) && o.results.length > 0 && 
    typeof (o.results[0] as Record<string, unknown>)?.path === 'string'
}

function isSessionsList(obj: unknown): obj is { 
  sessions: Array<{ sessionId: string; status: string; goal?: string; messageCount?: number }> 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return Array.isArray(o.sessions) && o.sessions.length > 0 &&
    typeof (o.sessions[0] as Record<string, unknown>)?.sessionId === 'string'
}

function isRunsList(obj: unknown): obj is { 
  runs: Array<{ runId: string; status: string; repoName?: string; goal?: string }> 
} {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return Array.isArray(o.runs) && o.runs.length > 0 &&
    typeof (o.runs[0] as Record<string, unknown>)?.runId === 'string'
}

function isErrorResponse(obj: unknown): obj is { success: false; error: string } {
  if (typeof obj !== 'object' || obj === null) return false
  const o = obj as Record<string, unknown>
  return o.success === false && typeof o.error === 'string'
}

// =============================================================================
// Data Transformers - Convert JSON to component props
// =============================================================================

function toRunSummary(data: { 
  action: 'run_triggered'
  data: { run_id: string; repository: string; goal: string }
  status: string 
}): RunSummary {
  return {
    runId: data.data.run_id,
    goal: data.data.goal,
    status: data.status as RunSummary['status'],
    repoName: data.data.repository,
    repoUrl: '',
    createdAt: new Date().toISOString(),
  }
}

function toCreatedTask(data: { 
  success: true
  sessionId: string
  status: string
  message: string
  url: string
  linkedWorkItem?: number 
}): CreatedTask {
  return {
    sessionId: data.sessionId,
    goal: data.message,
    workItemId: data.linkedWorkItem,
    createdAt: new Date().toISOString(),
  }
}

function toWorkItem(data: { 
  id: number
  title: string
  state: string
  type: string
  assignedTo?: string
  url?: string 
}): WorkItem {
  return {
    id: data.id,
    title: data.title,
    state: data.state,
    type: data.type,
    assignedTo: data.assignedTo,
    url: data.url || '',
  }
}

function toCodeSearchResults(data: { 
  results: Array<{ path: string; repository: string; matches: Array<{ line: number; content: string }> }>
}): CodeSearchResultItem[] {
  if (!data.results || !Array.isArray(data.results)) {
    return []
  }
  return data.results.flatMap(r => {
    // Handle cases where matches might not be an array
    const matches = Array.isArray(r.matches) ? r.matches : []
    return matches.map(m => ({
      repository: r.repository,
      filePath: r.path,
      lineNumber: m.line,
      content: m.content,
      matches: [],
    }))
  })
}

function toSessionList(data: { 
  sessions: Array<{ sessionId: string; status: string; goal?: string; messageCount?: number; createdAt?: string }> 
}): SessionListItem[] {
  return data.sessions.map(s => ({
    sessionId: s.sessionId,
    status: s.status as SessionListItem['status'],
    goal: s.goal || '',
    messageCount: s.messageCount || 0,
    createdAt: s.createdAt || new Date().toISOString(),
  }))
}

function toRunList(data: { 
  runs: Array<{ runId: string; status: string; repoName?: string; goal?: string; createdAt?: string }> 
}): RunListItem[] {
  return data.runs.map(r => ({
    runId: r.runId,
    status: r.status as RunListItem['status'],
    repoName: r.repoName || 'Unknown',
    goal: r.goal,
    createdAt: r.createdAt || new Date().toISOString(),
  }))
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Intelligent content renderer that detects structured JSON and renders
 * appropriate UI components for better UX.
 */
export const StructuredContentRenderer = memo(function StructuredContentRenderer({ content, className }: StructuredContentRendererProps) {
  // Memoize JSON parsing - this is potentially expensive
  // Note: useMemo must be called before any early returns (hooks rules)
  const parsed = useMemo(() => {
    if (!content || !content.trim()) return undefined
    return tryParseJson(content)
  }, [content])

  // Handle empty content
  if (!content || !content.trim()) {
    return null
  }
  
  // If not valid JSON, render as markdown
  if (parsed === null) {
    return (
      <div className={cn('text-sm', className)}>
        <MarkdownContent content={content} variant="default" />
      </div>
    )
  }

  // Detect and render structured content types
  
  // Action types (have "action" field)
  if (isRunTriggered(parsed)) {
    return <div className={className}><RunSummaryCard run={toRunSummary(parsed)} /></div>
  }

  if (isPlanCreated(parsed)) {
    // Transform various orchestrator formats to standard PlanCreatedData format
    let planData: PlanCreatedData
    
    if (parsed.data.file_changes || parsed.data.implementation_steps) {
      // File changes format: This is ONE task with detailed implementation breakdown
      // The summary IS the task, file_changes/implementation_steps are just details
      const goal = parsed.data.summary || parsed.data.goal || 'Plan'
      
      // Create a single sub-task representing the whole implementation
      planData = {
        goal: goal,
        sub_tasks: [{
          order: 1,
          description: goal
        }],
        repositories: parsed.data.repositories
      }
    } else if (parsed.data.plan && !parsed.data.sub_tasks) {
      // Convert plan.steps to sub_tasks format
      const goal = parsed.data.plan.title || parsed.data.goal || 'Plan'
      planData = {
        goal: goal,
        sub_tasks: (parsed.data.plan.steps || []).map((step, idx) => ({
          order: idx + 1,
          description: `${step.action}: ${step.description}`,
          repository: step.file
        })),
        repositories: parsed.data.repositories
      }
    } else {
      // Use existing data if it has required fields
      planData = {
        goal: parsed.data.goal || parsed.data.summary || 'Plan',
        sub_tasks: parsed.data.sub_tasks || [],
        repositories: parsed.data.repositories
      }
    }
    return <div className={className}><PlanCreatedCard data={planData} reasoning={parsed.reasoning} /></div>
  }

  if (isCorrectionPlanCreated(parsed)) {
    const correctionData: CorrectionPlanCreatedData = {
      correction_context: parsed.data.correction_context,
      original_plan_id: parsed.data.original_plan_id,
      fixes: parsed.data.fixes,
      constraints: parsed.data.constraints
    }
    return <div className={className}><CorrectionPlanCreatedCard data={correctionData} reasoning={parsed.reasoning} /></div>
  }

  if (isReviewComplete(parsed)) {
    return <div className={className}><ReviewCompleteCard data={parsed.data} reasoning={parsed.reasoning} /></div>
  }

  if (isReviewBlocked(parsed)) {
    return <div className={className}><ReviewBlockedCard data={{
      approved: false,
      reason: parsed.reason,
      details: parsed.details,
      recommendation: parsed.recommendation
    }} /></div>
  }

  if (isTestsAnalyzed(parsed)) {
    return <div className={className}><TestsAnalyzedCard data={parsed.data} reasoning={parsed.reasoning} warnings={parsed.warnings} /></div>
  }

  if (isClarificationNeeded(parsed)) {
    return <div className={className}><ClarificationNeededCard data={parsed.data} reasoning={parsed.reasoning} /></div>
  }

  // Response types (have specific field patterns)
  if (isTaskCreated(parsed)) {
    return <div className={className}><TaskCreatedCard task={toCreatedTask(parsed)} /></div>
  }

  if (isWorkItem(parsed)) {
    return <div className={className}><WorkItemCard workItem={toWorkItem(parsed)} /></div>
  }

  if (isCodeSearchResults(parsed)) {
    return <div className={className}><CodeSearchResult results={toCodeSearchResults(parsed)} /></div>
  }

  if (isSessionsList(parsed)) {
    return <div className={className}><SessionListResult sessions={toSessionList(parsed)} /></div>
  }

  if (isRunsList(parsed)) {
    return <div className={className}><RunListResult runs={toRunList(parsed)} /></div>
  }

  if (isErrorResponse(parsed)) {
    return <div className={className}><ErrorCard error={parsed.error} /></div>
  }

  // Unknown JSON structure - render as formatted JSON
  return <div className={className}><RawJsonCard data={parsed} /></div>
})
